/**
 * `/$org/apps/probe/interviews/$sessionId` — one saved interview.
 *
 * It opens on its review (`ProbeReview.tsx`): the verdict, the moments an
 * answer fell short in the person's own words, and the actions. A review still being written is read again until it lands; a
 * failed one can be asked for again. The transcript is one tab over, each
 * examiner line with the records it read as chips that open their rows on
 * the desk beside it (or, on a narrow screen, in a sheet); a moment sends
 * the reader to its line there, marked.
 *
 * The review's composer asks Amoreg about it in a new chat, the interview
 * named by id; the agent reads it with its own Probe tool. Export writes it
 * as Markdown.
 *
 * @module components/apps/probe/ProbeInterviewPage
 */
import type { ProbeExaminationArea } from "@amoreg/contracts";
import { Link, useNavigate } from "@tanstack/react-router";
import {
  ChevronDownIcon,
  ClipboardCopyIcon,
  DownloadIcon,
  TargetIcon,
} from "lucide-react";
import { useCallback, useEffect, useId, useMemo, useRef, useState } from "react";

import { useOrgChatProject } from "../../../hooks/useStartChatWithText";
import { writeTextToClipboard } from "../../../hooks/useCopyToClipboard";
import {
  readProbeInterview,
  retryProbeReview,
  type ProbeInterviewDetail,
  type ProbeInterviewTurn,
} from "../../../state/probeHistory";
import { PageHeader, SegmentedTabs } from "../../regulation/PageHeader";
import { ProbeSources } from "../../regulation/probe/ProbeSources";
import { Button } from "../../ui/button";
import { Menu, MenuItem, MenuPopup, MenuTrigger } from "../../ui/menu";
import { Sheet, SheetPopup } from "../../ui/sheet";
import { Skeleton } from "../../ui/skeleton";
import { toastManager } from "../../ui/toast";
import { cn } from "~/lib/utils";
import { areaLabel, interviewName } from "@amoreg/shared/probePractice";
import {
  areaSlug,
  askAboutInterviewText,
  copyInterviewToast,
  formatProbeDuration,
  interviewFileName,
  interviewMarkdown,
} from "./probeArea";
import { useProbeArea } from "./ProbeAreaProvider";
import {
  DESK_LABEL_CLASS,
  ProbeDeskButton,
  type ProbeDeskView,
  type ProbeDeskRenderProps,
  type ProbeStageDesk,
} from "./ProbeDesk";
import { savedDeskCall } from "./ProbeDesk.logic";
import { OpenRowsProvider } from "./desk/DeskParts";
import type { DeskRecord } from "./desk/deskRecords";
import { hierarchyStageDesk } from "./desk/HierarchyDesk";
import { lineRecordsOf } from "./desk/lineRecords";
import { ProbeRecordChips } from "./ProbeRecordChips";
import { interviewDateTime, ProbeEmpty, ProbeLoadError } from "./ProbeParts";
import { syncProbeInterview } from "./probeInterviewsStore";
import { ProbeReview, type ProbeReviewProps } from "./ProbeReview";
import { reviewPageOf } from "./ProbeReview.logic";

type DetailState =
  | { readonly kind: "loading" }
  | { readonly kind: "found"; readonly interview: ProbeInterviewDetail }
  | { readonly kind: "missing" }
  | { readonly kind: "failed" };

/** How long to wait before reading a review still being written again: gently, and less often as it runs. */
export const reviewPollDelayMs = (attempt: number): number =>
  Math.min(3_000 * 1.5 ** attempt, 20_000);

/** The most reads of a pending review: about nine minutes, past the API's five-minute stale window. */
export const REVIEW_POLL_LIMIT = 30;

/**
 * One interview, read for this organization; `retry` reads it again. While
 * its review is being written it is read again in the background, until it
 * lands, the interview is gone, or `REVIEW_POLL_LIMIT` reads; the API reads
 * a run pending past its stale window as failed, so that limit is a guard.
 * The interview list is refreshed whenever it holds this interview's review
 * in another state, so the sidebar takes the title.
 */
function useProbeInterviewDetail(organizationId: string | null, interviewId: string) {
  const [state, setState] = useState<{ readonly key: string; readonly value: DetailState } | null>(
    null,
  );
  const [attempt, setAttempt] = useState(0);
  const key = `${organizationId ?? ""}:${interviewId}:${attempt}`;
  useEffect(() => {
    if (organizationId === null) return;
    const controller = new AbortController();
    void readProbeInterview(organizationId, interviewId, controller.signal).then((read) => {
      if (controller.signal.aborted) return;
      setState({
        key,
        value: read.kind === "found" ? { kind: "found", interview: read.interview } : read,
      });
    });
    return () => controller.abort();
  }, [organizationId, interviewId, key]);
  const retry = useCallback(() => setAttempt((value) => value + 1), []);
  const value: DetailState = state?.key === key ? state.value : { kind: "loading" };
  const replace = useCallback(
    (interview: ProbeInterviewDetail) => setState({ key, value: { kind: "found", interview } }),
    [key],
  );

  const found = value.kind === "found" ? value.interview : null;
  useEffect(() => {
    if (organizationId !== null && found !== null) syncProbeInterview(organizationId, found);
  }, [organizationId, found]);

  const pending = found?.review?.status === "pending";
  const [polls, setPolls] = useState(0);
  useEffect(() => {
    if (!pending || organizationId === null || polls >= REVIEW_POLL_LIMIT) return;
    const controller = new AbortController();
    const timer = setTimeout(() => {
      void readProbeInterview(organizationId, interviewId, controller.signal).then((read) => {
        if (controller.signal.aborted || read.kind === "missing") return;
        if (read.kind === "found" && read.interview.review?.status !== "pending") {
          replace(read.interview);
        } else setPolls((count) => count + 1);
      });
    }, reviewPollDelayMs(polls));
    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [pending, polls, organizationId, interviewId, replace]);

  const [asking, setAsking] = useState(false);
  const retryReview = useCallback(() => {
    if (organizationId === null || value.kind !== "found") return;
    const interview = value.interview;
    setAsking(true);
    void retryProbeReview(organizationId, interviewId).then((review) => {
      setAsking(false);
      if (review === null) {
        toastManager.add({ type: "error", title: "Could not ask for the review again" });
        return;
      }
      setPolls(0);
      replace({ ...interview, review });
    });
  }, [organizationId, interviewId, value, replace]);

  return { state: value, retry, review: { run: retryReview, busy: asking } };
}

export function ProbeInterviewPage({ interviewId }: { readonly interviewId: string }) {
  const { organizationId, orgSegment } = useProbeArea();
  const { state, retry, review } = useProbeInterviewDetail(organizationId, interviewId);

  if (state.kind === "loading") return <InterviewSkeleton />;
  if (state.kind === "missing") {
    return (
      <>
        <PageHeader title="Interview" />
        <ProbeEmpty
          title="This interview isn't here"
          action={
            <Button
              variant="outline"
              size="sm"
              render={<Link to="/$org/apps/probe/interviews" params={{ org: orgSegment }} />}
            >
              All interviews
            </Button>
          }
        >
          It may belong to another organization, or the link is wrong.
        </ProbeEmpty>
      </>
    );
  }
  if (state.kind === "failed") {
    return (
      <>
        <PageHeader title="Interview" />
        <ProbeLoadError what="The interview" onRetry={retry} />
      </>
    );
  }
  return <InterviewView interview={state.interview} retry={review} />;
}

export type ProbeInterviewTab = "review" | "transcript";

export function InterviewView({
  interview,
  deskOf = hierarchyStageDesk,
  initialTab,
  retry,
}: {
  readonly interview: ProbeInterviewDetail;
  /** The Probe desk lab (`apps/web/e2e/probeDeskLab`) draws its desk variants here. */
  readonly deskOf?: ProbeStageDesk;
  /** The lab opens a frame on either tab. */
  readonly initialTab?: ProbeInterviewTab;
  /** Ask for a failed review again. */
  readonly retry?: ProbeReviewProps["retry"];
}) {
  const { organizationId, orgSegment, briefing } = useProbeArea();
  const navigate = useNavigate();
  const chatProject = useOrgChatProject(organizationId);
  const review = useMemo(() => reviewPageOf(interview), [interview]);
  const [tab, setTab] = useState<ProbeInterviewTab>(
    review === null ? "transcript" : (initialTab ?? "review"),
  );
  /** The transcript line a moment sent the reader to, marked there. */
  const [arrival, setArrival] = useState<string | null>(null);
  const call = useMemo(() => savedDeskCall(interview.turns), [interview.turns]);
  const desk = useMemo(() => deskOf(call, briefing), [deskOf, call, briefing]);
  const lineRecords = useMemo(() => lineRecordsOf(call, briefing), [call, briefing]);
  /** The desk shows a record read or a lookup that failed; a lookup that came back empty is neither. */
  const readSomething = interview.turns.some((turn) =>
    turn.lookups.some((lookup) => lookup.status === "failed" || lookup.records.length > 0),
  );

  const rehearse = (area: ProbeExaminationArea | null) =>
    void navigate({
      to: "/$org/apps/probe",
      params: { org: orgSegment },
      ...(area === null ? {} : { search: { focus: areaSlug(area) } }),
    });

  const download = () => {
    const blob = new Blob([interviewMarkdown(interview)], { type: "text/markdown;charset=utf-8" });
    const href = URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = href;
    anchor.download = interviewFileName(interview);
    anchor.click();
    setTimeout(() => URL.revokeObjectURL(href), 1_000);
  };
  const copy = () => {
    void copyInterviewToast(() =>
      writeTextToClipboard(interviewMarkdown(interview), "interview"),
    ).then((toast) => toastManager.add(toast));
  };

  const toTranscript = (turnKey: string) => {
    setArrival(turnKey);
    setTab("transcript");
  };

  const facts = [
    interviewDateTime(interview.createdAt),
    interview.durationMs === null ? "" : formatProbeDuration(interview.durationMs),
    interview.turns.length > 0 ? `${interview.turns.length} turns` : "",
  ].filter((part) => part.length > 0);

  return (
    <>
      <PageHeader
        {...(review === null ? { title: interviewName(interview) } : {})}
        tabs={
          review === null ? undefined : (
            <SegmentedTabs
              label="Interview"
              value={tab}
              onValueChange={(next) => {
                setArrival(null);
                setTab(next);
              }}
              tabs={[
                { value: "review", label: "Review" },
                { value: "transcript", label: "Transcript" },
              ]}
            />
          )
        }
        actions={
          <>
            <Menu>
              <MenuTrigger
                render={
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-muted-foreground dark:text-foreground/65"
                  />
                }
                data-probe-export=""
              >
                <DownloadIcon className="size-3.5" />
                <span className="max-sm:sr-only">Export</span>
                <ChevronDownIcon className="size-3 opacity-70" />
              </MenuTrigger>
              <MenuPopup align="end" className="w-56">
                <MenuItem onClick={download}>
                  <DownloadIcon className="size-3.5" />
                  Download as Markdown
                </MenuItem>
                <MenuItem onClick={copy}>
                  <ClipboardCopyIcon className="size-3.5" />
                  Copy as Markdown
                </MenuItem>
              </MenuPopup>
            </Menu>
            {review !== null ? (
              <Button
                size="sm"
                variant="outline"
                onClick={() => rehearse(review.rehearse)}
                title={review.rehearse === null ? undefined : `Rehearse ${areaLabel(review.rehearse)} again`}
                data-probe-rehearse={review.rehearse ?? ""}
              >
                <TargetIcon className="size-3.5" />
                <span className="max-sm:sr-only">Rehearse again</span>
              </Button>
            ) : null}
          </>
        }
      />
      <div
        className="flex min-h-0 flex-1 flex-col"
        data-probe-interview={interview.id}
        data-probe-tab={tab}
      >
        {tab === "review" && review !== null ? (
          <ProbeReview
            interviewId={interview.id}
            title={interviewName(interview)}
            facts={{
              date: interviewDateTime(interview.createdAt),
              length:
                interview.durationMs === null ? null : formatProbeDuration(interview.durationMs),
            }}
            page={review}
            orgSegment={orgSegment}
            lineRecords={lineRecords}
            onRehearse={rehearse}
            onTranscript={toTranscript}
            ask={
              chatProject === null
                ? null
                : {
                    project: {
                      id: chatProject.id,
                      environmentId: chatProject.environmentId,
                      defaultModelSelection: chatProject.defaultModelSelection,
                    },
                    frame: (question) => askAboutInterviewText(interview, question),
                  }
            }
            {...(retry === undefined ? {} : { retry })}
          />
        ) : (
          <InterviewTranscript
            interview={interview}
            title={review === null ? null : interviewName(interview)}
            lead={review === null ? { facts, summary: interview.summary?.trim() || null } : null}
            desk={readSomething ? desk : null}
            lineRecords={lineRecords}
            orgSegment={orgSegment}
            arrival={arrival}
          />
        )}
      </div>
    </>
  );
}

/**
 * The conversation as it was said, with what the examiner read under each
 * line it informed, and the desk of everything read beside it; below the
 * desk's width, the desk is behind a button and opens in a sheet.
 */
function InterviewTranscript({
  interview,
  title,
  lead,
  desk,
  lineRecords,
  orgSegment,
  arrival,
}: {
  readonly interview: ProbeInterviewDetail;
  /** The page's title, over the transcript when the header does not carry it. */
  readonly title: string | null;
  /** Over the transcript when the interview has no takeaways to carry them: its facts and summary. */
  readonly lead: { readonly facts: ReadonlyArray<string>; readonly summary: string | null } | null;
  /** Null when the interview kept no lookups (saved before they were kept, or none ran). */
  readonly desk: ProbeDeskView<ProbeDeskRenderProps> | null;
  readonly lineRecords: ReadonlyMap<string, ReadonlyArray<DeskRecord>>;
  readonly orgSegment: string;
  /** The line a review moment sent the reader to: scrolled to and marked. */
  readonly arrival: string | null;
}) {
  const sheetHeadingId = useId();
  const [sheetOpen, setSheetOpen] = useState(false);
  const column = useRef<HTMLElement>(null);
  const showDesk = useCallback(() => {
    if (column.current?.checkVisibility?.() !== true) setSheetOpen(true);
  }, []);
  return (
    <OpenRowsProvider onShow={showDesk}>
      <Arrive arrival={arrival} />
      <div className="@container/interview flex min-h-0 flex-1 flex-col">
        <div
          className={cn(
            "flex min-h-0 flex-1 flex-col",
            desk !== null && "@4xl/interview:grid @4xl/interview:grid-cols-[minmax(0,1fr)_340px]",
          )}
        >
          <section
            aria-label="Transcript"
            className="relative min-h-0 flex-1 overflow-y-auto px-4 pt-6 pb-10 sm:px-6 @4xl/interview:pt-8"
            data-probe-transcript=""
          >
            {desk !== null ? (
              <div className="sticky top-0 z-10 -mt-2 mb-2 flex justify-end @4xl/interview:hidden">
                <ProbeDeskButton
                  read={desk.read}
                  onClick={() => setSheetOpen(true)}
                  className="bg-background"
                />
              </div>
            ) : null}
            <div className="mx-auto flex w-full max-w-3xl flex-col gap-1">
              {title !== null ? (
                <h1 className="px-3 pb-3 text-xl leading-7 font-semibold tracking-[-0.01em] text-foreground text-balance">
                  {title}
                </h1>
              ) : null}
              {lead !== null ? (
                <div className="flex flex-col gap-2 px-3 pb-4">
                  {lead.facts.length > 0 ? (
                    <p className={cn("text-xs tabular-nums", DESK_LABEL_CLASS)} data-probe-facts="">
                      {lead.facts.join(" · ")}
                    </p>
                  ) : null}
                  {lead.summary !== null ? (
                    <p
                      className="max-w-[68ch] text-[15px] leading-relaxed text-foreground text-pretty"
                      data-probe-summary=""
                    >
                      {lead.summary}
                    </p>
                  ) : null}
                </div>
              ) : null}
              <ol className="flex flex-col gap-1">
                {interview.turns.map((turn, index) => {
                  const yours = turn.role !== "assistant";
                  const records = lineRecords.get(String(index));
                  const unread = turn.sources.filter(
                    (source) =>
                      !(records ?? []).some((record) => record.key === `web:${source.url}`),
                  );
                  return (
                    <li
                      key={index}
                      className={cn(
                        "flex scroll-mt-4 flex-col gap-1.5 rounded-lg p-2 transition-colors",
                        yours ? "items-end" : "items-start",
                        arrival === String(index) &&
                          "bg-amber-100/70 ring-1 ring-amber-300/70 dark:bg-amber-300/10 dark:ring-amber-300/30",
                      )}
                      data-probe-turn={turn.role}
                      data-probe-turn-key={String(index)}
                      {...(arrival === String(index) ? { "data-probe-arrived": "" } : {})}
                    >
                      <h3 className="sr-only">{speakerOf(turn.role)}</h3>
                      {/* Speech, transcribed: plain text for both speakers. */}
                      <p
                        className={cn(
                          "whitespace-pre-wrap text-sm leading-relaxed text-foreground",
                          yours ? "max-w-[85%] rounded-2xl bg-message px-3.5 py-2.5" : "px-1",
                        )}
                      >
                        {turn.text.trim()}
                      </p>
                      {records !== undefined ? (
                        <ProbeRecordChips
                          records={records}
                          className={cn("mt-0", yours ? "justify-end pe-1" : "ps-1")}
                        />
                      ) : null}
                      {unread.length > 0 ? (
                        // Pages kept without their lookup (saved before lookups were, or past
                        // the save's lookup limit): linked as the call linked them.
                        <div className={cn("-mt-1", yours ? "pe-1" : "ps-1")}>
                          <ProbeSources sources={unread} />
                        </div>
                      ) : null}
                    </li>
                  );
                })}
              </ol>
            </div>
          </section>
          {desk !== null ? (
            <aside
              ref={column}
              aria-label="The examiner's file"
              className="hidden min-h-0 flex-col border-s border-border/60 bg-card/40 @4xl/interview:flex"
              data-probe-saved-desk=""
            >
              {desk.render({ orgSegment })}
            </aside>
          ) : null}
        </div>
      </div>
      {desk !== null ? (
        <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
          <SheetPopup side="right" aria-labelledby={sheetHeadingId}>
            {desk.render({ orgSegment, headingId: sheetHeadingId })}
          </SheetPopup>
        </Sheet>
      ) : null}
    </OpenRowsProvider>
  );
}

/** On arriving from a review moment: its line brought into view. */
function Arrive({ arrival }: { readonly arrival: string | null }) {
  useEffect(() => {
    if (arrival === null) return;
    const still = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches ?? true;
    document
      .querySelector?.(`[data-probe-turn-key="${arrival}"]`)
      ?.scrollIntoView({ block: "center", behavior: still ? "auto" : "smooth" });
  }, [arrival]);
  return null;
}

const speakerOf = (role: ProbeInterviewTurn["role"]) => (role === "assistant" ? "Examiner" : "You");

function InterviewSkeleton() {
  return (
    <div
      className="flex min-h-0 flex-1 flex-col"
      aria-busy="true"
      aria-label="Loading the interview"
    >
      <div className="flex min-h-[52px] items-center gap-3 border-b border-border/60 px-4 sm:px-6">
        <Skeleton className="h-4 w-72 max-w-[60%]" />
      </div>
      <div className="mx-auto flex w-full max-w-5xl flex-col gap-8 px-4 pt-6 sm:px-8 sm:pt-8">
        <div className="flex flex-col gap-3">
          <Skeleton className="h-8 w-32" />
          <Skeleton className="h-3.5 w-full max-w-[68ch]" />
          <Skeleton className="h-3.5 w-[70%] max-w-[48ch]" />
        </div>
        <div className="flex flex-col gap-2">
          <Skeleton className="h-3 w-20" />
          <Skeleton className="h-20 w-full rounded-lg" />
        </div>
      </div>
    </div>
  );
}
